import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Dashboard from "../Layout/Dashboard";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { currencyFormat } from "../utils";
import { useGetBillingDetail } from "../hooks/queries";
import { FileUpload } from "primereact/fileupload";
import { BASE_URL } from "../constants";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
// import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Messages } from "primereact/messages";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import BillingHistoryDialog from "./BillingHistoryDialog";

const PaymentDetailView = () => {
  const { billingID } = useParams();
  const { data, isLoading } = useGetBillingDetail({ billingID });
  return (
    <Dashboard
      headerTitle="Detalle del pago"
      // backButtonUrl={backButtonUrl ?? "/casos"}
      dynamicGoBack
    >
      {isLoading && <ProgressSpinner />}
      {data && !isLoading && <PaymentDetailContent data={data} />}
      {!data && !isLoading && <p>No se encontró el caso médico</p>}
    </Dashboard>
  );
};

const PaymentDetailContent = ({ data }) => {
  const toastRef = useRef(null);
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const [status, setStatus] = useState(data.status);
  const [payments, setPayments] = useState(data?.payments || []);
  const [showObservationDialog, setShowObservationDialog] = useState(false);
  const [observation, setObservation] = useState("");
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const msgs = useRef(null);

  const statusOptions = [
    { label: "Pendiente", value: "pending", severity: "danger" },
    { label: "En Revisión", value: "under_review", severity: "warning" },
    { label: "Pagado", value: "paid", severity: "success" },
  ];

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newStatus) =>
      fetch(`${BASE_URL}insurance/billing/${data.id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          status: newStatus,
          paid_observation: newStatus === "paid" ? observation : undefined,
        }),
      }),
    onSuccess: (_, newStatus) => {
      toastRef.current.show({
        severity: "success",
        summary: "Éxito",
        detail: "Estado del pago actualizado exitosamente.",
      });
      queryClient.invalidateQueries(["billingDetail", data.id]);
      setStatus(newStatus);
      setShowObservationDialog(false);
      setObservation("");
    },
    onError: () => {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo actualizar el estado del pago.",
      });
    },
  });

  const getStatusTag = (status) => {
    const selectedStatus = statusOptions.find(
      (option) => option.value === status
    );
    return (
      <Tag severity={selectedStatus?.severity} value={selectedStatus?.label} />
    );
  };

  const onUpload = () => {
    toastRef.current.show({
      severity: "success",
      summary: "Éxito",
      detail: "Archivo subido con éxito",
    });
    mutation.mutate("under_review"); // Cuando la clínica suba un archivo debe cambiar a estatus EN REVISIÓN
  };

  const onPaymentConfirm = () => {
    if (status === "paid") {
      toastRef.current.show({
        severity: "info",
        summary: "Información",
        detail: "El pago ya está confirmado.",
      });
      return;
    }

    mutation.mutate("under_review");
    setStatus("under_review");
  };

  const onAdminConfirmPayment = () => {
    if (status === "paid") {
      toastRef.current.show({
        severity: "info",
        summary: "Información",
        detail: "El pago ya está confirmado.",
      });
      return;
    }

    mutation.mutate("paid");
    setStatus("paid");
  };

  return (
    <>
      <Toast ref={toastRef} position="top-center" />

      <div className="p-fluid">
        <div className="flex justify-content-end">
          <Button
            className="flex flex-column w-fit bg-transparent text-600 border-0"
            raised
            tooltip="Ver historial"
            tooltipOptions={{ position: "top" }}
            onClick={() => setShowHistoryDialog(true)}
          >
            <span>Estatus:</span>
            {getStatusTag(status)}
          </Button>
        </div>
        <h3 className="text-center">Detalles del Pago</h3>
        <p className="text-2xl font-bold text-center">{`Monto a pagar (USD): ${currencyFormat(
          data?.amount_usd
        )}`}</p>

        <div className="p-field">
          <p className="m-0 text-lg">
            <strong>Nombre de la empresa:</strong>{" "}
            <span>CITA MEDICA 24/7 C.A</span>
          </p>
          <p className="m-0 text-lg">
            <strong>Banco:</strong> <span>Mercantil (0105)</span>
          </p>
          <p className="font-bold text-lg">Pago móvil:</p>

          <div className="p-mb-2 flex align-items-center gap-1">
            <div>
              <p className="m-0">
                <strong>Número de Teléfono:</strong>{" "}
                <span>{"0412-3034004"}</span>
              </p>
            </div>
            <div>
              <Button
                label="Copiar"
                icon="pi pi-copy"
                className="p-button-text"
                onClick={() => navigator.clipboard.writeText("0412-3034004")}
              />
            </div>
          </div>
          <div className="p-mb-2 flex align-items-center gap-1">
            <div>
              <p className="m-0">
                <strong>RIF:</strong> <span>J-503151277</span>
              </p>
            </div>
            <div>
              <Button
                label="Copiar"
                icon="pi pi-copy"
                className="p-button-text"
                onClick={() => navigator.clipboard.writeText("J-503151277")}
              />
            </div>
          </div>
          {/* <div className="p-mb-3c flex">
                    <div className="flex-auto">
                      <p className="m-0">
                        <strong>Banco:</strong> 0105 <span>Mercantil</span>
                      </p>
                    </div>
                    <div className="w-3">
                      <Button
                        label="Copiar"
                        icon="pi pi-copy"
                        className="p-button-text"
                        onClick={() => navigator.clipboard.writeText("0105")}
                      />
                    </div>
                  </div> */}
        </div>
        <div className="p-field">
          <p className="">
            <strong> Transferencia:</strong>
            <span> 01050113071113124164 </span>{" "}
          </p>
        </div>

        <div className="p-field">
          <p className="font-bold text-lg">Instrucciones:</p>
          <ol className="pl-4">
            <li>
              Debe de pagar el monto mostrado en USD a la tasa del día, el cuál
              será verificado usando la tasa del BCV de la fecha que aparece en
              el pago.
            </li>
            <li>
              Para verificar su pago el comprobante debe contener visible la
              fecha, referencia de la transacción, el monto.
            </li>
            <li>
              Evite retrasos en la confirmación de su pago para mantener su
              cuenta activa.
            </li>
          </ol>
        </div>

        <div className="p-field">
          <Messages ref={msgs} />
          <FileUpload
            name="payment_support"
            mode="basic"
            chooseLabel={"Subir Comprobante"}
            onSelect={(e) => {}}
            url={`${BASE_URL}insurance/billing-payments/`}
            auto
            accept=".jpeg,.jpg,.png,.pdf"
            onBeforeUpload={({ formData, xhr }) => {
              formData.append("billing", data.id);
              formData.append("amount", data.id);
              formData.append("reference", data.id);
              xhr.addEventListener("readystatechange", () => {
                if (xhr.readyState === XMLHttpRequest.OPENED) {
                  xhr.setRequestHeader(
                    "Authorization",
                    `Token ${localStorage.getItem("token")}`
                  );
                }
              });
              const reqListener = (r) => {
                console.log("rrr", r);
                console.log(r.target.response);
                // convert the JSON string to a JSON object
                const data = JSON.parse(r.target.response);
                setPayments((prev) => [
                  ...prev,
                  {
                    id: data.id,
                    payment_support: data.payment_support,
                  },
                ]);

                // setInvoice((prev) => [
                //   ...prev,
                //   {
                //     file: data.file,
                //     fileName: data.file_name,
                //     id: data.id,
                //   },
                // ]);
              };
              console.log(xhr);

              xhr.addEventListener("load", reqListener);
              console.log(localStorage.getItem("token"));
            }}
            onUpload={onUpload}
            onBeforeSend={(e) => {
              console.log("onBeforeSend", e);
            }}
            onValidationFail={(file) => {
              if (file.size > 1000000) {
                msgs.current.show([
                  {
                    sticky: true,
                    severity: "error",
                    summary: "Tamaño de archivo inválido",
                    detail: "El tamaño máximo permitido es 1MB",
                  },
                ]);
                // e.options.clear();
                return;
              }
            }}
            maxFileSize={1000000} // 5MB
            invalidFileSizeMessageDetail="El tamaño máximo permitido es 1MB"
          />
        </div>

        {!!data?.payments?.length && (
          <div className="p-field">
            <h4>Comprobantes Subidos</h4>
            <DataTable stripedRows value={payments} className="mt-2">
              <Column
                header="Vista previa"
                body={(row) => (
                  <Button
                    label="Ver"
                    className="max-w-fit"
                    outlined
                    onClick={() => window.open(row.payment_support, "_blank")}
                  />
                )}
              />
              <Column
                header="Nombre"
                body={(row) => (
                  <Button
                    className="text-left"
                    label={row.id}
                    link
                    onClick={() => window.open(row.payment_support, "_blank")}
                  />
                )}
              />
              <Column
                header="Acción"
                body={(row) => (
                  <Button
                    label="Eliminar"
                    className="max-w-fit"
                    outlined
                    text
                    severity="danger"
                    aria-label="Eliminar"
                    onClick={() => {
                      console.log(row);
                      // Aquí puedes agregar la lógica para eliminar el comprobante
                    }}
                  />
                )}
              />
            </DataTable>
          </div>
        )}

        {/* {isAdmin && (
          <div>
            <div className="p-field">
              <label htmlFor="statusDropdown">Cambiar Estatus</label>
              <Dropdown
                id="statusDropdown"
                value={status}
                options={statusOptions}
                onChange={(e) => setStatus(e.value)}
                placeholder="Seleccionar Estatus"
              />
            </div>
          </div>
        )} */}

        <div className="mt-2 p-dialog-footer justify-content-end flex">
          <Button
            severity="success"
            label="Confirmar Pago"
            icon="pi pi-check"
            onClick={onPaymentConfirm}
            disabled={status === "paid" || payments.length === 0}
            tooltip="Confirma el pago una vez que hayas subido el comprobante"
            tooltipOptions={{ position: "top", showOnDisabled: true }}
          />
        </div>

        {isAdmin && (
          <div className="mt-2 p-dialog-footer justify-content-end flex">
            <Button
              severity="success"
              label="Confirmar Pago como Administrador"
              icon="pi pi-check"
              onClick={() => setShowObservationDialog(true)}
            />
          </div>
        )}
        {isAdmin && (
          <Dialog
            header="Agrega una observación"
            draggable={false}
            visible={showObservationDialog}
            onHide={() => {
              if (!mutation.isPending) {
                setShowObservationDialog(false);
              }
            }}
            style={{ width: "33vw" }}
          >
            <div className="p-field flex flex-column gap-1">
              <label htmlFor="observation">Observación (obligatoria):</label>
              <InputTextarea
                id="observation"
                className="w-full"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Ingrese una observación"
                autoResize
              />
            </div>
            <div className="flex justify-content-end">
              <Button
                label="Guardar"
                icon="pi pi-save"
                loading={mutation.isPending}
                disabled={mutation.isPending || !observation}
                onClick={onAdminConfirmPayment}
              />
            </div>
          </Dialog>
        )}
        <BillingHistoryDialog
          billingId={data.id}
          visible={showHistoryDialog}
          onHide={() => setShowHistoryDialog(false)}
          getStatusTag={getStatusTag}
        />
      </div>
    </>
  );
};

export default PaymentDetailView;
